import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { BaseDataService, HttpResponsesHandlersFactory, IHttpResponsesHandler } from "@bbwt/modules/data-service";
import { UserLogins } from ".";
import { LinkUserResult } from "./link-user-result";
import { UserService } from "./user.service";

@Injectable({
    providedIn: "root"
})
export class LinkUserService extends BaseDataService {
    static readonly UserOrganizationDelinkedEventName = "UserOrganizationDelinkedEventName";
    static readonly UserSelectedOrganizationChangedEventName = "UserSelectedOrganizationChangedEventName";
    readonly url = "api/linked-user";

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private broadcastService: BroadcastService,
        private userService: UserService
    ) {
        super(http, handlersFactory);
    }

    getUserLogins(): Promise<UserLogins[]> {
        return this.handleRequest(
            this.http.get<UserLogins[]>(this.url),
            this.handlersFactory.getDefault({
                showSuccessMessage: false,
                errorMessage: "Error loading the linked users information"
            })
        );
    }

    linkUser(email: string): Promise<LinkUserResult> {
        return this.handleRequest(
            this.http.post<LinkUserResult>(`${this.url}/link`, { email }),
            this.handlersFactory.getDefault({
                showSuccessMessage: false
            })
        );
    }

    async delinkUser(email: string): Promise<any> {
        await this.handleRequest(
            this.http.post(`${this.url}/delink-user`, { email }),
            this.handlersFactory.getDefault({
                showSuccessMessage: false
            })
        );
        this.notifyUserOrganizationDelinked();
        await this.userService.refreshCurrentUser();
    }

    checkToken(userId: string, token: string, handler?: IHttpResponsesHandler): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/check?userId=${userId}&token=${token}`), handler);
    }

    confirm(userId: string, token: string): Promise<any> {
        return this.handleRequest(
            this.http.post(`${this.url}/confirm`, { userId, token }),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
    }

    async switchUser(loginId: string, organizationId: string): Promise<any> {
        await this.handleRequest(
            this.http.post(`${this.url}/switch`, { loginId, organizationId }),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );

        await this.userService.refreshCurrentUser();
        this.notifyUserSelectedOrganizationChanged();
    }

    async switchPatientPassport(): Promise<any> {
        await this.handleRequest(
            this.http.post(`${this.url}/switch-passport`, null),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );

        await this.userService.refreshCurrentUser();
        this.notifyUserSelectedOrganizationChanged();
    }

    async delinkOrganization(userId: string, organizationId: string): Promise<any> {
        await this.handleRequest(
            this.http.post(`${this.url}/delink-organization`, { loginId: userId, organizationId: organizationId }),
            this.handlersFactory.getDefault({ showSuccessMessage: false })
        );
        this.notifyUserOrganizationDelinked();
    }

    private notifyUserOrganizationDelinked() {
        this.broadcastService.broadcast(LinkUserService.UserOrganizationDelinkedEventName);
    }

    private notifyUserSelectedOrganizationChanged() {
        this.broadcastService.broadcast(LinkUserService.UserSelectedOrganizationChangedEventName);
    }
}
